import { createContext, useContext } from "react";

/**
 * One-shot signal carrying the `refined_from_message_id` from the
 * `ContextEnrichmentCard` (the producer) to the `chat-model-adapter`
 * (the consumer). The card sets the value immediately before calling
 * `aui.thread().append(...)`; the adapter reads it at the top of
 * `run()` and clears it so a subsequent composer-sent user turn does
 * not inherit the marker.
 *
 * The setter also accepts `undefined` so the card can roll back the
 * ref if the `append(...)` call throws synchronously; otherwise a
 * later composer-sent turn would inherit the stale marker and the
 * backend would flip `refined=true` on an unrelated message.
 *
 * `subscribeRefineFailed` is the return path: when the adapter's
 * `/completion` call exits pre-stream for any reason (network failure,
 * 4xx/5xx, or user-initiated abort), the atomic backend bundle never
 * committed and the source `refined` flag is still false. Listeners are
 * notified with the message id so the session-scoped optimistic "Refined
 * above" mark can be rolled back. Mid-stream errors are excluded
 * deliberately: by that point the commit has already persisted.
 *
 * Implementing this as a React context (rather than a module-level
 * ref) scopes the signal to one `RuntimeProvider` instance, so multiple
 * chat surfaces on the same page do not cross-contaminate.
 */
export interface RefineContextValue {
    setRefinedFromMessageId: (messageId: number | undefined) => void;
    subscribeRefineFailed: (
        listener: (messageId: number) => void
    ) => () => void;
}

export const RefineContext = createContext<RefineContextValue | null>(null);

export function useRefineContext(): RefineContextValue {
    const ctx = useContext(RefineContext);
    if (ctx === null) {
        throw new Error(
            "useRefineContext must be used inside a <RuntimeProvider>"
        );
    }
    return ctx;
}
